import { useState, useCallback, useEffect, useContext, useMemo } from "react";

import { firebaseDB, getDateTimeNow } from "firebase/client";

import {
  baseUrl,
  capitalize,
  copyToClipboard,
  entryMatchesSearch,
  getAnswer,
  normalizeText,
  sortByProperty,
  sortDescByProperty,
  ToReadableDate,
} from "helpers";

import { AlertContext } from "contexts/AlertContext";

import useEntryForm from "hooks/useEntryForm";

import { FormStatus, MessageType } from "model/dictionary";

import {
  FormStatus as FormStatusOptions,
  StaffPositions,
  States,
} from "model/options";

// `getLabel` de `helpers` asume que el valor está en el catálogo y revienta si
// no: acá los campos son opcionales (una postulación vieja puede no tener
// provincia o cargo), así que se cae al valor crudo.
const toLabel = (options, value) => {
  const option = options.find((item) => item.value === value);

  return option ? option.label : value || "";
};

// Los rotulos que arman el nombre del archivo traen acentos, parentesis y
// barras (el premio se rotula "Premio (Tematica)"): `normalizeText` saca
// mayusculas y diacriticos, y el resto se colapsa en guiones bajos.
const slugify = (value) =>
  normalizeText(value)
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

// El título de la pregunta va tal cual de encabezado, sólo colapsando saltos
// de línea y espacios repetidos para que la fila de encabezados quede en una
// línea.
const toColumnName = (value) => `${value || ""}`.replace(/\s+/g, " ").trim();

// Ancho de cada columna en caracteres, según lo más largo que lleve (título
// incluido), entre un mínimo para que los encabezados cortos no queden
// apretados y un tope para que las respuestas largas no ocupen la pantalla.
const MIN_COLUMN_WIDTH = 12;
const MAX_COLUMN_WIDTH = 60;

const columnWidths = (rows) => {
  const widths = {};

  rows.forEach((row) => {
    Object.entries(row).forEach(([column, value]) => {
      widths[column] = Math.max(
        widths[column] || column.length,
        ...`${value}`.split("\n").map((line) => line.length)
      );
    });
  });

  return Object.values(widths).map((width) => ({
    wch: Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, width)),
  }));
};

// Las `multiple` guardan la respuesta en las opciones tildadas, no en `value`,
// que es lo único que mira `getAnswer`.
const toAnswer = (question) => {
  if (question.questionType === "multiple") {
    return (question.options || [])
      .filter((option) => option.checked)
      .map((option) => option.label || option.value)
      .join("; ");
  }

  const answer = getAnswer(question);

  return answer === undefined || answer === null ? "" : `${answer}`;
};

// Una columna por pregunta, con el título como encabezado. Si el cuestionario
// repite un título (en dos secciones, por ejemplo), la segunda lleva un número
// para que no pise a la primera.
const answerColumns = (entry) => {
  const columns = {};

  (entry.sections || []).forEach((section) => {
    (section.questions || []).forEach((question) => {
      const title = toColumnName(question.title) || "Pregunta";

      let column = title;

      for (let count = 2; column in columns; count += 1) {
        column = `${title} (${count})`;
      }

      columns[column] = toAnswer(question);
    });
  });

  return columns;
};

/**
 * Listado de postulaciones de administración: filtros que reconstruyen la
 * query, paginado del lado del cliente, exportación a Excel, la confirmación
 * manual de las que quedaron en borrador (el postulante no entró al link del
 * mail, o el mail nunca le llegó) y el borrado.
 */
export default function useEntriesList() {
  const { showWaiting, hideWaiting, showAlert } = useContext(AlertContext);

  const { UpdateForm } = useEntryForm();

  // Arranca en `true` para que el listado muestre el esqueleto desde el primer
  // render, sin el parpadeo de una tarjeta vacía antes del primer fetch.
  const [loading, setLoading] = useState(true);

  const [awards, setAwards] = useState([]);

  const [award, setAward] = useState();
  const [status, setStatus] = useState();
  const [competition, setCompetition] = useState();

  const [entries, setEntries] = useState([]);

  const [competitions, setCompetitions] = useState([]);

  // Edición con la que abre el listado (y a la que vuelve "Limpiar"): la del
  // año en curso o, si todavía no se creó, la más nueva.
  const [defaultCompetition, setDefaultCompetition] = useState("");

  const [filter, setFilter] = useState([]);

  // La búsqueda por texto no va a Firestore —no tiene un "contiene"—: se aplica
  // sobre el resultado ya traído, igual que los filtros de los toolbars de
  // CRUD, así que tipear no dispara queries.
  const [search, setSearch] = useState("");

  // El campo de búsqueda es no controlado (`InputField` usa `defaultValue`):
  // "Limpiar" cambia esta clave para remontarlo vacío, si no el texto quedaría
  // en pantalla con la lista ya sin filtrar.
  const [searchKey, setSearchKey] = useState(0);

  // La primera query espera a que se resuelva ese default. Sin esto el listado
  // arrancaría trayéndose todas las ediciones —los documentos de `entries` son
  // gordos— para descartarlas un instante después.
  const [filterReady, setFilterReady] = useState(false);

  const [emptyMessage, setEmptyMessage] = useState("");

  // Paginado del lado del cliente sobre el resultado del filtro, igual que el
  // resto de las pantallas de administración (`components/Pagination`).
  const [page, setPage] = useState(1);

  const pageSize = 10;

  // "Limpiar" deja el listado como lo abre: sin premio ni estado, pero sobre la
  // edición por default. Para ver el histórico completo hay que elegir "Todos
  // los años" a mano.
  const resetFilter = () => {
    setFilter(
      defaultCompetition
        ? [
            {
              field: "competition.id",
              operator: "==",
              value: defaultCompetition,
            },
          ]
        : []
    );

    setAward("");
    setCompetition(defaultCompetition);
    setStatus("");

    setSearch("");
    setSearchKey((prev) => prev + 1);

    setPage(1);
  };

  const handleSearch = (field, value) => {
    setSearch(normalizeText(value));

    setPage(1);
  };

  const updateFilter = (field, operator, value) => {
    if (field === "award.id") {
      setAward(value);
    }

    if (field === "currentStatus.type") {
      setStatus(value);
    }

    if (field === "competition.id") {
      setCompetition(value);
    }

    let newFilter = filter.filter((item) => {
      return item.field !== field;
    });

    if (value !== "") {
      newFilter = [
        ...newFilter,
        { field: field, operator: operator, value: value },
      ];
    }

    setFilter(newFilter);

    setPage(1);
  };

  const loadAwards = useCallback(async () => {
    var collection = firebaseDB.collection("topics");

    let query = collection.where("actived", "==", true).orderBy("title");

    const topicSnapshot = await query.get();

    topicSnapshot.docs.forEach(async (topic) => {
      const awardSnapshot = await firebaseDB
        .collection(`topics/${topic.id}/awards`)
        .where("actived", "==", true)
        .orderBy("title")
        .get();

      const items = awardSnapshot.docs.map((award) => {
        return {
          value: award.id,
          label: capitalize(`${award.data().title} (${topic.data().title})`),
        };
      });

      setAwards((prevAwards) =>
        prevAwards.concat(items).sort(sortByProperty("label"))
      );
    });
  }, []);

  // No toca `loading`: el esqueleto lo maneja la query de postulaciones, que es
  // la que arranca recién cuando esto termina de resolver el año por default.
  const loadCompetitions = useCallback(() => {
    let query = firebaseDB.collection("competitions");

    query
      .get()
      .then(({ docs }) => {
        // Firestore devuelve por id de documento: se ordena por año, de la
        // edición más nueva a la más vieja.
        const competition = docs
          .map((doc) => {
            return { value: doc.id, label: doc.data().title };
          })
          .sort(sortDescByProperty("label"));

        setCompetitions(competition);

        // La edición vigente es la del año en curso; si todavía no se creó (o
        // el año ya pasó) queda la más nueva, que es la primera de la lista.
        const currentYear = `${new Date().getFullYear()}`;

        const preselected =
          competition.find((item) => item.label === currentYear) ||
          competition[0];

        if (preselected) {
          setDefaultCompetition(preselected.value);
          setCompetition(preselected.value);

          setFilter([
            {
              field: "competition.id",
              operator: "==",
              value: preselected.value,
            },
          ]);
        }
      })
      .catch(function (error) {
        console.error(error);
      })
      .finally(() => {
        // Aun si la lectura falló el listado tiene que cargar: queda sin
        // default, mostrando todas las ediciones.
        setFilterReady(true);
      });
  }, []);

  // El filtro entero en una sola query: el paginado es del lado del cliente,
  // así que el listado necesita el conjunto completo (es también el que exporta
  // el Excel, que antes repetía esta misma query).
  const getEntries = useCallback(async () => {
    if (!filterReady) {
      return;
    }

    setLoading(true);

    showWaiting("Cargando las postulaciones...");

    let query = firebaseDB
      .collection("entries")
      .orderBy("createdAt", "desc")
      .where("actived", "==", true);

    filter.forEach((item) => {
      query = query.where(item.field, item.operator, item.value);
    });

    try {
      const { docs } = await query.get();

      const items = docs.map((doc) => {
        return { ...doc.data(), id: doc.id };
      });

      setEntries(items);

      setEmptyMessage(
        items.length === 0 ? "No hay resultados para el filtro seleccionado" : ""
      );
    } catch (error) {
      console.error(error);
    } finally {
      hideWaiting();
      setLoading(false);
    }
  }, [filter, filterReady, hideWaiting, showWaiting]);

  // Todo lo que sigue —paginado, total, Excel— trabaja sobre este conjunto, no
  // sobre el de la query.
  const filteredEntries = useMemo(
    () =>
      search ? entries.filter((entry) => entryMatchesSearch(entry, search)) : entries,
    [entries, search]
  );

  const handleFilter = (field, value) => {
    updateFilter(field, "==", value);
  };

  const confirmEntry = useCallback(
    async (entry) => {
      showWaiting("Confirmando la postulación...");

      // `id` es el id del documento, no un campo del documento: sacarlo evita
      // grabarlo adentro cuando `UpdateForm` reescribe la postulación entera.
      const { id, ...form } = entry;

      try {
        // Mismo camino que recorre el postulante desde el link del mail: suma
        // `confirmed` al historial de estados y lo deja como estado actual.
        const updated = await UpdateForm(
          { ...form, identity: form.identity || id },
          FormStatus.CONFIRMED
        );

        // Se toca sólo la fila, sin repetir la query. Si el filtro pide un
        // estado puntual la postulación ya no pertenece al listado y se saca;
        // si no, se reemplaza para que el rótulo de estado y las acciones
        // queden al día.
        setEntries((prev) =>
          status && status !== FormStatus.CONFIRMED
            ? prev.filter((item) => item.id !== id)
            : prev.map((item) => (item.id === id ? { ...updated, id } : item))
        );

        showAlert(MessageType.SUCESSFULL, "Postulación confirmada", 2000);
      } catch (error) {
        console.error(error);

        showAlert(MessageType.DANGER, `Se produjo un error ${error}`, 3000);
      }
    },
    [UpdateForm, showAlert, showWaiting, status]
  );

  // El mismo link que lleva el mail de confirmación (`sendConfirmEmail` de
  // `useEntryForm`): abre la postulación para revisarla y confirmarla. Sirve
  // para cuando el mail no llega y hay que pasárselo al postulante por otro
  // lado —teléfono, WhatsApp—, sin tener que armarlo a mano.
  const copyConfirmationLink = useCallback(
    async (entry) => {
      // `identity` es el id con el que se arma el link; las postulaciones
      // viejas no lo tienen y caen al id del documento, igual que la
      // confirmación manual.
      const link = `${baseUrl()}/editar-postulacion/${
        entry.identity || entry.id
      }`;

      try {
        await copyToClipboard(link);

        showAlert(MessageType.SUCESSFULL, "Link de confirmación copiado", 2000);
      } catch (error) {
        console.error(error);

        // Si el navegador no deja copiar, que al menos quede a la vista para
        // copiarlo a mano: es la única forma de recuperarlo desde acá.
        showAlert(
          MessageType.WARNING,
          `No se pudo copiar automáticamente. El link es: ${link}`,
          8000
        );
      }
    },
    [showAlert]
  );

  const deleteEntry = useCallback(async (entry) => {
    showWaiting("Borrando la postulación...");

    try {
      // Soft-delete, como el resto de las colecciones: el documento queda
      // grabado pero sale de todas las queries (filtran `actived == true`), así
      // que si se borra por error se recupera volviendo el flag a `true`.
      await firebaseDB
        .collection("entries")
        .doc(entry.id)
        .set({ actived: false, modifiedAt: getDateTimeNow() }, { merge: true });

      // Igual que la confirmación manual: se toca la lista en memoria en vez de
      // repetir la query.
      setEntries((prev) => prev.filter((item) => item.id !== entry.id));

      showAlert(MessageType.SUCESSFULL, "Postulación borrada", 2000);
    } catch (error) {
      console.error(error);

      showAlert(MessageType.DANGER, `Se produjo un error ${error}`, 3000);
    }
  }, [showAlert, showWaiting]);

  // Exporta el resultado completo del filtro —no la página a la vista—, con los
  // datos de la cabecera de cada postulación: los de la ficha, que son los
  // mismos para todos los premios. Con un premio elegido suma además una
  // columna por pregunta del cuestionario: las preguntas cambian de premio en
  // premio, así que mezclando premios no alinean y quedan afuera.
  //
  // Sale como `.xlsx` y no como CSV: el CSV separado por comas se abría "todo
  // junto" en el Excel en español (que espera punto y coma), y separado por
  // punto y coma pasaba lo mismo en el Excel en inglés. El Excel real no tiene
  // separador ni problemas de codificación, y lo abren igual Excel, Google
  // Sheets y LibreOffice.
  const exportEntries = async () => {
    showWaiting("Exportando las postulaciones...");

    const withAnswers = Boolean(award);

    const selectedAward = awards.find((item) => item.value === award);

    const selectedCompetition = competitions.find(
      (item) => item.value === competition
    );

    const fileName = [
      "postulaciones",
      selectedCompetition ? selectedCompetition.label : "todas_las_ediciones",
      selectedAward && selectedAward.label,
      status && toLabel(FormStatusOptions, status),
      withAnswers && "respuestas",
    ]
      .filter(Boolean)
      .map(slugify)
      .join("_");

    const data = filteredEntries.map((entry) => ({
      "N°": entry.entryNumber || "",
      Estado: toLabel(
        FormStatusOptions,
        entry.currentStatus && entry.currentStatus.type
      ),
      Edición: entry.competition ? entry.competition.title : "",
      Temática: entry.topic ? entry.topic.title : "",
      Premio: entry.award ? entry.award.title : "",
      Proyecto: entry.projectName || "",
      "Empresa/Institución": entry.companyName || "",
      "Sitio Web": entry.website || "",
      Nombre: entry.firstName || "",
      Apellido: entry.lastName || "",
      Cargo: toLabel(StaffPositions, entry.staffPosition),
      Email: entry.email || "",
      Teléfono: entry.phone || "",
      Provincia: toLabel(States, entry.state),
      // Las postulaciones viejas guardaban la localidad como el objeto del
      // autocomplete, no como texto.
      Localidad: (entry.city && entry.city.label) || entry.city || "",
      LinkedIn: entry.linkedin || "",
      Instagram: entry.instagram || "",
      Facebook: entry.facebook || "",
      X: entry.twitter || "",
      // `postulator*` es quien completó el formulario cuando postula a un
      // tercero; el responsable de más arriba es el postulado.
      "Postulado por": entry.showPostulator
        ? entry.postulatorFullName || ""
        : "",
      "Email de quien postula": entry.showPostulator
        ? entry.postulatorEmail || ""
        : "",
      "Teléfono de quien postula": entry.showPostulator
        ? entry.postulatorPhone || ""
        : "",
      Creada: entry.createdAt ? ToReadableDate(entry.createdAt) : "",
      ...(withAnswers ? answerColumns(entry) : {}),
    }));

    try {
      // SheetJS pesa unos 400 KB y sólo lo usa este botón: se carga recién
      // acá para que no vaya en el bundle que bajan los visitantes.
      const XLSX = await import("xlsx");

      const sheet = XLSX.utils.json_to_sheet(data);

      sheet["!cols"] = columnWidths(data);

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, sheet, "Postulaciones");

      XLSX.writeFile(workbook, `${fileName}.xlsx`);
    } catch (error) {
      console.error(error);

      showAlert(MessageType.DANGER, `Se produjo un error ${error}`, 3000);
    } finally {
      hideWaiting();
    }
  };

  useEffect(() => {
    getEntries();
  }, [getEntries]);

  useEffect(() => {
    loadAwards();
  }, [loadAwards]);

  useEffect(() => {
    loadCompetitions();
  }, [loadCompetitions]);

  // La página se recorta contra el total: si se confirma la última postulación
  // de la última página, el listado no queda en una página vacía.
  const totalPages = Math.max(1, Math.ceil(filteredEntries.length / pageSize));
  const currentPage = Math.min(page, totalPages);

  const pageEntries = filteredEntries.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return {
    loading,
    entries: pageEntries,
    totalEntries: filteredEntries.length,
    pageSize,
    currentPage,
    setPage,
    awards,
    handleFilter,
    competitions,
    resetFilter,
    award,
    status,
    competition,
    emptyMessage:
      emptyMessage ||
      (entries.length > 0 && filteredEntries.length === 0
        ? "No hay postulaciones que coincidan con la búsqueda"
        : ""),
    handleSearch,
    searchKey,
    exportEntries,
    confirmEntry,
    copyConfirmationLink,
    deleteEntry,
  };
}
